import React, { useContext, useEffect, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import { Box } from '@mui/material';
import SaveButton from '../Common/Buttons/SaveButton';
import moment from 'moment';
import {
  IMsbReport,
  useMsbReportsQuery,
  useScheduleReportMutation,
  MsbReportsDocument
} from '../../generated/graphql';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import { formatDate } from '../../utils/ui';
import { DATE_TIME_FORMATS } from '../../utils/constants';
import { PropagateLoader } from 'react-spinners';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Colors } from '../../utils/theme';

export type IErrors = {
  cacheDate: string | null;
};

export const ErrorTexts = {
  cacheDate: 'Date is required'
};

const defaultErrorsObj: IErrors = {
  cacheDate: null
};

const minAllowedDate = new Date(2000, 0, 1);

const GenerateCache: React.FunctionComponent = () => {
  const { dispatch } = useContext(AppContext);
  const [cacheDate, setCacheDate] = useState<Date | null>(null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const { data, loading, startPolling } = useMsbReportsQuery();
  const [reports, setReports] = useState(data?.result || []);
  const [scheduleReport] = useScheduleReportMutation();

  useEffect(() => {
    startPolling(10000);
  }, []);

  useEffect(() => {
    if (data?.result) {
      setReports(data.result);
    }
  }, [data?.result]);

  const handleCacheDateChange = (value: Date | null) => {
    if (!value) {
      setFormErrors({ ...formErrors, cacheDate: ErrorTexts.cacheDate });
    } else {
      setFormErrors({ ...formErrors, cacheDate: null });
      setCacheDate(value);
    }
  };

  const submitForm = () => {
    const errors = {
      cacheDate: null
    } as IErrors;

    let hasErrors = false;
    if (!cacheDate) {
      hasErrors = true;
      errors.cacheDate = ErrorTexts.cacheDate;
    }

    if (hasErrors) {
      setFormErrors({ ...errors });
    } else {
      const date = moment(cacheDate).utc(true).format('YYYY-MM-DD');
      const isLast = isLastWorkingDay();
      if (isLast) {
        if (canSchedule()) {
          setCacheDate(null);
          const input = {
            report_date: date
          };
          scheduleReport({
            variables: { input },
            refetchQueries: [
              {
                query: MsbReportsDocument,
                variables: {}
              }
            ]
          });
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Report for ${date} has been scheduled`
            }
          });
        } else {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'info',
              text: `Report for ${date} is being generated`
            }
          });
        }
      } else {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'warning',
            text: 'You should select last working day of a past month'
          }
        });
      }
    }
  };

  const canSchedule = () => {
    let result = true;
    const date = moment(cacheDate).utc(true).format('YYYY-MM-DD');
    const report = reports.find((r) => r?.report_date === date && r.generation_end === null);
    if (report) {
      result = false;
    }
    return result;
  };

  const isLastWorkingDay = () => {
    const clonedDate = moment(cacheDate);
    const now = moment();
    if (clonedDate.isAfter(now)) {
      return false;
    }

    const lastDayOfMonth = clonedDate.endOf('month');
    while ((lastDayOfMonth.isoWeekday() >= 1 && lastDayOfMonth.isoWeekday() <= 5) === false) {
      lastDayOfMonth.subtract(1, 'day');
    }
    const date = moment(cacheDate);
    const isSame = lastDayOfMonth.isSame(date, 'day');
    return isSame;
  };

  const renderGenerationEnd = (rowData: IMsbReport) =>
    rowData.generation_end ? (
      formatDate(rowData.generation_end, DATE_TIME_FORMATS.DATE_AND_TIME)
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <PropagateLoader color={Colors.gradient} />
      </Box>
    );

  const columns: IHeadCell[] = [
    {
      title: 'Report Date',
      field: 'report_date',
      type: 'date',
      render: (rowData: IMsbReport) => formatDate(rowData.report_date)
    },
    {
      title: 'Generation Start',
      field: 'generation_start',
      type: 'date',
      render: (rowData: IMsbReport) =>
        formatDate(rowData.generation_start, DATE_TIME_FORMATS.DATE_AND_TIME)
    },
    {
      title: 'Generation End',
      field: 'generation_end',
      type: 'date',
      render: renderGenerationEnd
    }
  ];

  return (
    <Box className="overviewPage">
      <Box className="print\:w-full">
        <Box
          sx={{
            p: '30px 0px 0px 30px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="generate-cache-date"
              label="Cache Date"
              value={cacheDate}
              onChange={handleCacheDateChange}
              errorMsg={formErrors.cacheDate}
              minDate={minAllowedDate}
            />
          </LocalizationProvider>
          <SaveButton id="run-cache-button" onClick={submitForm} text="Run Report" />
        </Box>
        <OverviewTable columns={columns} data={reports} loading={loading} hideSearch />
      </Box>
    </Box>
  );
};

export default GenerateCache;
